import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import {useTranslation} from 'react-i18next';
import {PayloadStates} from '@lore/utils';
import {
  Accordion,
  InContextHelpMessage,
  LinkButton,
  ListSelect,
  Loader,
  ProjectSharingSuggestions,
  SocialShareButtons
} from '@communityshare/ui-shared';
import DialogTemplate from '@/dialogs_common/DialogTemplate';
import CloseDialogButton from '@/dialogs_common/CloseDialogButton';
import useDialogLauncher from '@/dialogs-routable/_hooks/useDialogLauncher';
import {DialogManager} from '@/dialogs_new/DialogManager';
import useActiveEvents from '@/features/share-event/hooks/useActiveEvents';
import EventLinkPreview from './EventLinkPreview';

// Brand name is not translatable, so it is interpolated into the strings.
const BRAND = 'CommunityShare';

EventShareDialog.propTypes = {
  // When omitted, the user picks from their own active events.
  event: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    state: PropTypes.string,
    data: PropTypes.shape({
      title: PropTypes.string
    })
  }),
  onCancel: PropTypes.func.isRequired
};

export default function EventShareDialog(props) {
  const { event: fixedEvent, onCancel } = props;
  const { t } = useTranslation();
  const launch = useDialogLauncher();
  const isEventFixed = Boolean(fixedEvent);

  const activeEvents = useActiveEvents();
  const [selectedEventId, setSelectedEventId] = useState(fixedEvent?.id ?? null);

  // Default the selection to the first active event once the list loads.
  useEffect(() => {
    if (!isEventFixed && !selectedEventId && activeEvents.data?.length > 0) {
      setSelectedEventId(activeEvents.data[0].id);
    }
  }, [isEventFixed, selectedEventId, activeEvents.data]);

  if (isEventFixed && fixedEvent.state === PayloadStates.FETCHING) {
    return (
      <DialogTemplate>
        <Loader />
      </DialogTemplate>
    );
  }

  if (!isEventFixed && activeEvents.state === PayloadStates.FETCHING) {
    return (
      <DialogTemplate>
        <Loader />
      </DialogTemplate>
    );
  }

  const event = isEventFixed ?
    fixedEvent :
    (activeEvents.data || []).find((activeEvent) => activeEvent.id === selectedEventId);

  if (!event) {
    return (
      <DialogTemplate className="w-screen max-w-screen-lg !bg-cs-gray-100">
        <CloseDialogButton onCancel={onCancel} />
        <div className="px-6 pt-6 pb-8 sm:px-8">
          <div className="mt-5">
            <LinkButton
              label={t('events.share.back_to_home')}
              icon="left"
              onClick={onCancel}
            />
          </div>
          <p className="mt-6 text-base text-cs-gray-500">
            {t('events.share.no_active_events')}
          </p>
        </div>
      </DialogTemplate>
    );
  }

  const shareUrl = `${window.location.origin}/share/event/${event.id}`;

  function onEmail() {
    window.location.href = `mailto:?subject=${encodeURIComponent(event.data.title)}&body=${encodeURIComponent(shareUrl)}`;
  }

  function onLinkedIn() {
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`, '_blank');
  }

  function onFacebook() {
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`, '_blank');
  }

  function onCopyLink() {
    navigator.clipboard.writeText(shareUrl);
  }

  return (
    <DialogTemplate className="w-screen max-w-screen-lg !bg-cs-gray-100">
      <CloseDialogButton onCancel={onCancel} />
      <div className="px-6 pt-6 pb-8 sm:px-8">
        <p className="text-base text-cs-gray-500">
          {t('events.share.breadcrumb_parent')}
          <span className="mx-2 text-cs-gray-300">|</span>
          <span className="text-cs-gray-900">{t('events.share.breadcrumb_action')}</span>
        </p>
        <div className="mt-5">
          <LinkButton
            label={isEventFixed ? t('events.share.back_to_event') : t('events.share.back_to_home')}
            icon="left"
            onClick={onCancel}
          />
        </div>
        <h2 className="mt-2 text-3xl font-semibold text-cs-gray-900">
          {t('events.share.title')}
        </h2>
        <p className="mt-2 text-base text-cs-gray-500">
          {t('events.share.subtitle')}
        </p>
        <div className="mt-6 flex flex-col gap-1">
          <label className="text-base text-cs-gray-900">
            {t('events.share.field_label')}
          </label>
          <ListSelect
            value={event}
            options={activeEvents.data || []}
            disabled={isEventFixed}
            getOptionLabel={(activeEvent) => activeEvent.data.title}
            getOptionKey={(activeEvent) => activeEvent.id}
            onChange={(activeEvent) => setSelectedEventId(activeEvent.id)}
            className="bg-white"
          />
        </div>
        <div className="mt-4">
          <InContextHelpMessage
            boldText={t('events.share.callout_bold')}
            bodyText={t('events.share.callout_body')}
            linkLabel={t('events.share.edit_event_title')}
            onLinkClick={() => launch(DialogManager.EventTitleEdit.Config(event.id))}
          />
        </div>
        <div className="mt-6">
          {/* key remounts the preview on event change so event-specific
              state (selected image, default-save guard) resets. */}
          <EventLinkPreview key={event.id} event={event} />
        </div>
        <div className="mt-4">
          <Accordion>
            <ProjectSharingSuggestions
              options={[
                {
                  key: 'find_partners',
                  label: t('events.share.option_find_partners'),
                  template: t('events.share.template_find_partners', {title: event.data.title || 'INCOMPLETE'})
                },
                {
                  key: 'spread_word',
                  label: t('events.share.option_spread_word', { brand: BRAND }),
                  template: t('events.share.template_spread_word', { brand: BRAND })
                }
              ]}
              examples={[
                { id: 'example-1', text: t('events.share.example_1') },
                { id: 'example-2', text: t('events.share.example_2') },
                { id: 'example-3', text: t('events.share.example_3', { brand: BRAND }) }
              ]}
              helpTitle={t('events.share.help_title')}
              viewSuggestionsLabel={t('events.share.view_suggestions')}
              helpQuestion={t('events.share.help_question')}
              templateLabel={t('events.share.template_label')}
              resetTemplateLabel={t('events.share.reset_template')}
              copyLabel={t('events.share.copy_text')}
              copiedLabel={t('events.share.copied')}
              seeExamplesLabel={t('events.share.see_examples')}
            />
          </Accordion>
        </div>
        <div className="mt-6">
          <SocialShareButtons
            onEmail={onEmail}
            onLinkedIn={onLinkedIn}
            onFacebook={onFacebook}
            onCopyLink={onCopyLink}
            copiedLinkLabel={t('controls.Copied')}
            copyLinkLabel={t('controls.Copy link')}
          />
        </div>
      </div>
    </DialogTemplate>
  );
}
